import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Zap, X, Move, Settings, Wrench, RefreshCw,
  Package, Search, QrCode, ChevronRight, AlertCircle,
  Loader2, CheckCircle, AlertTriangle
} from 'lucide-react';
import { api, Asset, ScanResult, PendingAction } from '../lib/api';
import QrScanner from './QrScanner';

// ============================================================================
// CONFIGURATIONS
// ============================================================================

const quickActions = [
  { id: 'move', label: 'Sposta Asset', icon: Move, color: 'from-green-500 to-emerald-600', description: 'Avvia uno spostamento' },
  { id: 'maintenance', label: 'Manutenzione', icon: Settings, color: 'from-teal-500 to-cyan-600', description: 'Registra manutenzione' },
  { id: 'repair', label: 'Riparazione', icon: Wrench, color: 'from-amber-500 to-orange-600', description: 'Registra riparazione' },
  { id: 'status', label: 'Cambia Stato', icon: RefreshCw, color: 'from-purple-500 to-indigo-600', description: 'Modifica stato asset' }
];

const statusOptions = [
  { value: 'active', label: 'Attivo' },
  { value: 'in_storage', label: 'In Magazzino' },
  { value: 'disposed', label: 'Dismesso' },
  { value: 'lost', label: 'Perso' }
];

const conditionOptions = [
  { value: 'excellent', label: 'Eccellente', emoji: '✨' },
  { value: 'good', label: 'Buono', emoji: '👍' },
  { value: 'fair', label: 'Discreto', emoji: '👌' },
  { value: 'poor', label: 'Scarso', emoji: '⚠️' },
  { value: 'broken', label: 'Rotto', emoji: '❌' }
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function QuickActionButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<'action' | 'asset' | 'form' | 'blocked'>('action');
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [pendingActionForAsset, setPendingActionForAsset] = useState<PendingAction | null>(null);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState<any>({});
  
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch assets for search
  const { data: assetsData, isLoading: assetsLoading } = useQuery({
    queryKey: ['assets'],
    queryFn: () => api.getAssets(),
    enabled: isOpen && step === 'asset'
  });

  // Fetch pending actions to check for conflicts
  const { data: pendingActionsData } = useQuery({
    queryKey: ['pending-actions'],
    queryFn: () => api.getPendingActions(),
    enabled: isOpen
  });

  // Fetch locations for move action
  const { data: locationsData } = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.getLocations(),
    enabled: isOpen && selectedAction === 'move'
  });

  // Action mutation
  const actionMutation = useMutation({
    mutationFn: (params: { assetId: string; action: string; data: any }) =>
      api.updateAsset(params.assetId, { action: params.action, ...params.data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['pending-actions'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      handleClose();
    }
  });

  const handleClose = () => {
    setIsOpen(false);
    setStep('action');
    setSelectedAction(null);
    setSelectedAsset(null);
    setPendingActionForAsset(null);
    setSearchQuery('');
    setFormData({});
  };

  const handleSelectAction = (actionId: string) => {
    setSelectedAction(actionId);
    setStep('asset');
  };

  // Check if asset has pending action
  const checkPendingAction = (asset: Asset): PendingAction | null => {
    if (!pendingActionsData?.pendingActions) return null;
    return pendingActionsData.pendingActions.find(
      (pa: PendingAction) => pa.asset.id === asset.id
    ) || null;
  };

  const handleSelectAsset = (asset: Asset) => {
    // Check for pending action on this asset
    const pending = checkPendingAction(asset);
    if (pending) {
      setSelectedAsset(asset);
      setPendingActionForAsset(pending);
      setStep('blocked');
      return;
    }

    setSelectedAsset(asset);
    setPendingActionForAsset(null);
    // Initialize form data based on action - isCompleted defaults to false
    if (selectedAction === 'move') {
      setFormData({ locationId: '', notes: '', isCompleted: false });
    } else if (selectedAction === 'maintenance' || selectedAction === 'repair') {
      setFormData({ description: '', newCondition: 'none', cost: 0, isCompleted: false });
    } else if (selectedAction === 'status') {
      setFormData({ status: '', condition: 'none', notes: '' });
    }
    setStep('form');
  };

  const handleQrScanResult = async (result: ScanResult) => {
    if (result.found && result.type === 'asset' && result.id) {
      // Fetch full asset data
      try {
        const assetData = await api.getAsset(result.id);
        handleSelectAsset(assetData.asset as Asset);
        setShowQrScanner(false);
      } catch (error) {
        console.error('Error fetching asset:', error);
      }
    }
  };

  const handleSubmit = () => {
    if (!selectedAsset || !selectedAction) return;

    let actionName = selectedAction;
    let actionData = { ...formData };

    // Transform action names to match API
    if (selectedAction === 'status') {
      actionName = 'changeStatus';
    }

    // Convert cost to correct format
    if (actionData.cost) {
      actionData.cost = parseFloat(actionData.cost);
    }

    actionMutation.mutate({
      assetId: selectedAsset.id,
      action: actionName,
      data: actionData
    });
  };

  // Filter assets by search query
  const filteredAssets = assetsData?.assets.filter(asset =>
    asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    asset.qrCode.toLowerCase().includes(searchQuery.toLowerCase())
  ).slice(0, 20) || [];

  // Filter available locations (exclude current)
  const availableLocations = locationsData?.locations.filter(
    loc => selectedAsset ? loc.id !== selectedAsset.locationId : true
  ) || [];

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-full shadow-lg shadow-amber-500/30 flex items-center justify-center transition-all hover:scale-105 active:scale-95"
        aria-label="Azione Rapida"
      >
        <Zap className="w-5 h-5" />
      </button>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-50 bg-black/50 animate-fadeIn"
        onClick={handleClose}
      />
      
      {/* Modal - Opens from right on desktop, from bottom on mobile, positioned above nav bar */}
      <div className="fixed inset-x-4 bottom-[5rem] lg:inset-auto lg:right-4 lg:bottom-20 lg:w-96 z-50 bg-white rounded-2xl shadow-2xl overflow-hidden animate-slideInUp max-h-[calc(100vh-8rem)] lg:max-h-[calc(100vh-6rem)]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center">
              <Zap className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">
                {step === 'action' && 'Azione Rapida'}
                {step === 'asset' && 'Seleziona Asset'}
                {step === 'form' && quickActions.find(a => a.id === selectedAction)?.label}
                {step === 'blocked' && 'Azione in Corso'}
              </h2>
              <p className="text-xs text-gray-500">
                {step === 'action' && 'Scegli cosa vuoi fare'}
                {step === 'asset' && 'Scansiona o cerca l\'asset'}
                {step === 'form' && selectedAsset?.name}
                {step === 'blocked' && 'Completa prima l\'azione esistente'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 max-h-[60vh] overflow-y-auto">
          {/* Step 1: Select Action */}
          {step === 'action' && (
            <div className="grid grid-cols-2 gap-3">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.id}
                    onClick={() => handleSelectAction(action.id)}
                    className={`p-4 rounded-xl bg-gradient-to-br ${action.color} text-white text-left transition-all hover:scale-[1.02] active:scale-[0.98] shadow-md`}
                  >
                    <Icon className="w-6 h-6 mb-2" />
                    <p className="font-semibold text-sm">{action.label}</p>
                    <p className="text-xs opacity-80 mt-0.5">{action.description}</p>
                  </button>
                );
              })}
            </div>
          )}

          {/* Step 2: Select Asset */}
          {step === 'asset' && (
            <div className="space-y-4">
              {/* QR Scan Option */}
              <button
                onClick={() => setShowQrScanner(true)}
                className="w-full p-4 bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200 rounded-xl flex items-center gap-3 hover:border-primary-300 transition-colors"
              >
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <QrCode className="w-5 h-5 text-primary-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-primary-900">Scansiona QR Code</p>
                  <p className="text-xs text-primary-600">Modo più veloce</p>
                </div>
                <ChevronRight className="w-5 h-5 text-primary-400 ml-auto" />
              </button>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cerca per nome o codice QR..."
                  className="input pl-10"
                  autoFocus
                />
              </div>

              {/* Assets List */}
              {assetsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                </div>
              ) : filteredAssets.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    {searchQuery ? 'Nessun asset trovato' : 'Inizia a cercare...'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {filteredAssets.map((asset) => (
                    <button
                      key={asset.id}
                      onClick={() => handleSelectAsset(asset)}
                      className="w-full p-3 bg-gray-50 hover:bg-gray-100 rounded-xl flex items-center gap-3 text-left transition-colors"
                    >
                      <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
                        <Package className="w-5 h-5 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{asset.name}</p>
                        <p className="text-xs text-gray-500 font-mono">{asset.qrCode}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </button>
                  ))}
                </div>
              )}

              {/* Back Button */}
              <button
                onClick={() => setStep('action')}
                className="w-full btn btn-secondary"
              >
                ← Indietro
              </button>
            </div>
          )}

          {/* Step 3: Action Form */}
          {step === 'form' && selectedAsset && (
            <div className="space-y-4">
              {/* Asset Info */}
              <div className="p-3 bg-gray-50 rounded-xl flex items-center gap-3">
                <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-gray-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{selectedAsset.name}</p>
                  <p className="text-xs text-gray-500 font-mono">{selectedAsset.qrCode}</p>
                </div>
              </div>

              {/* Move Form */}
              {selectedAction === 'move' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Nuova ubicazione <span className="text-red-500">*</span></label>
                    <select
                      value={formData.locationId}
                      onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                      className="input"
                    >
                      <option value="">Seleziona ubicazione...</option>
                      {availableLocations.map((loc) => (
                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Note (opzionale)</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="input"
                      rows={2}
                      placeholder="Motivo dello spostamento..."
                    />
                  </div>
                  <div className="form-group">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.isCompleted}
                        onChange={(e) => setFormData({ ...formData, isCompleted: e.target.checked })}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">Spostamento completato</span>
                    </label>
                    {!formData.isCompleted && (
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        L'asset sarà "In Transito" fino al completamento
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* Maintenance/Repair Form */}
              {(selectedAction === 'maintenance' || selectedAction === 'repair') && (
                <>
                  <div className="form-group">
                    <label className="form-label">Descrizione <span className="text-red-500">*</span></label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="input"
                      rows={2}
                      placeholder="Descrivi l'intervento..."
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Condizione dopo intervento</label>
                    <select
                      value={formData.newCondition}
                      onChange={(e) => setFormData({ ...formData, newCondition: e.target.value })}
                      className="input"
                    >
                      <option value="none">Nessun cambio</option>
                      {conditionOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.emoji} {opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.isCompleted}
                        onChange={(e) => setFormData({ ...formData, isCompleted: e.target.checked })}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">Intervento completato</span>
                    </label>
                    {!formData.isCompleted && (
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        L'asset sarà in {selectedAction === 'maintenance' ? 'manutenzione' : 'riparazione'}
                      </p>
                    )}
                  </div>
                  {formData.isCompleted && (
                    <div className="form-group">
                      <label className="form-label">Costo (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.cost || ''}
                        onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                        className="input"
                        placeholder="0.00"
                      />
                    </div>
                  )}
                </>
              )}

              {/* Status Change Form */}
              {selectedAction === 'status' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Nuovo stato <span className="text-red-500">*</span></label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="input"
                    >
                      <option value="">Seleziona stato...</option>
                      {statusOptions.filter(s => s.value !== selectedAsset.status).map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Nuova condizione</label>
                    <select
                      value={formData.condition}
                      onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                      className="input"
                    >
                      <option value="none">Nessun cambio</option>
                      {conditionOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.emoji} {opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Note (opzionale)</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="input"
                      rows={2}
                      placeholder="Motivo del cambio..."
                    />
                  </div>
                </>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setStep('asset');
                    setSelectedAsset(null);
                  }}
                  className="flex-1 btn btn-secondary"
                >
                  ← Indietro
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={
                    actionMutation.isPending ||
                    (selectedAction === 'move' && !formData.locationId) ||
                    ((selectedAction === 'maintenance' || selectedAction === 'repair') && !formData.description?.trim()) ||
                    (selectedAction === 'status' && !formData.status)
                  }
                  className="flex-1 btn btn-primary disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {actionMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Salvataggio...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Conferma
                    </>
                  )}
                </button>
              </div>

              {/* Error */}
              {actionMutation.isError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {(actionMutation.error as Error)?.message || 'Errore durante l\'operazione'}
                </div>
              )}
            </div>
          )}

          {/* Step: Blocked - Asset has pending action */}
          {step === 'blocked' && selectedAsset && pendingActionForAsset && (
            <div className="space-y-4">
              {/* Warning */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-900 mb-1">Azione già in corso</h3>
                    <p className="text-sm text-amber-700">
                      Questo asset ha già un'azione in sospeso. Devi completarla prima di poter avviare una nuova azione.
                    </p>
                  </div>
                </div>
              </div>

              {/* Asset Info */}
              <div className="p-3 bg-gray-50 rounded-xl flex items-center gap-3">
                <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-gray-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{selectedAsset.name}</p>
                  <p className="text-xs text-gray-500 font-mono">{selectedAsset.qrCode}</p>
                </div>
              </div>

              {/* Pending Action Info */}
              <div className="p-3 bg-white border border-gray-200 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Azione in sospeso:</p>
                <div className="flex items-center gap-2">
                  {pendingActionForAsset.actionType === 'moved' && (
                    <>
                      <Move className="w-4 h-4 text-indigo-600" />
                      <span className="font-medium text-indigo-700">Spostamento</span>
                      {pendingActionForAsset.toLocation && (
                        <span className="text-sm text-gray-600">→ {pendingActionForAsset.toLocation.name}</span>
                      )}
                    </>
                  )}
                  {pendingActionForAsset.actionType === 'maintenance' && (
                    <>
                      <Settings className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-blue-700">Manutenzione</span>
                    </>
                  )}
                  {pendingActionForAsset.actionType === 'repair' && (
                    <>
                      <Wrench className="w-4 h-4 text-orange-600" />
                      <span className="font-medium text-orange-700">Riparazione</span>
                    </>
                  )}
                </div>
                {pendingActionForAsset.description && (
                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">{pendingActionForAsset.description}</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setStep('asset');
                    setSelectedAsset(null);
                    setPendingActionForAsset(null);
                  }}
                  className="flex-1 btn btn-secondary"
                >
                  ← Altro Asset
                </button>
                <Link
                  to={`/assets/${selectedAsset.id}`}
                  onClick={handleClose}
                  className="flex-1 btn btn-primary flex items-center justify-center gap-2"
                >
                  <ChevronRight className="w-4 h-4" />
                  Vai all'Asset
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* QR Scanner */}
      {showQrScanner && (
        <QrScanner
          isOpen={showQrScanner}
          onClose={() => setShowQrScanner(false)}
          onScanResult={handleQrScanResult}
          navigateOnScan={false}
          title="Scansiona Asset"
          hint="Inquadra il QR code dell'asset"
        />
      )}
    </>
  );
}
